import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { camelCaseObject, snakeCaseObject } from '@edx/frontend-platform/utils';

/**
 * Learner-facing subscription REST calls (blendxapi). The backend resolves the
 * org from the session; a course is identified by its openedx course id, a
 * program by its Hasura uuid.
 */
const baseUrl = () => `${getConfig().LMS_BASE_URL}/blendxapi/subscriptions`;

const client = () => getAuthenticatedHttpClient();

/** Identify a product for query params / request bodies. */
const productParams = ({ productType, courseId, programId }) => (
  productType === 'program'
    ? { product_type: 'program', product_id: programId }
    : { product_type: 'course', openedx_course_id: courseId }
);

/** All active subscription plans (every interval) for a product. */
export const getSubscriptionPlans = async ({ productType, courseId, programId }) => {
  const { data } = await client().get(`${baseUrl()}/plans/`, {
    params: productParams({ productType, courseId, programId }),
  });
  return camelCaseObject(data).plans || [];
};

/** The learner's subscriptions for a product (to show status), newest first. */
export const getMySubscriptionsForProduct = async ({ productType, courseId, programId }) => {
  const { data } = await client().get(`${baseUrl()}/`, {
    params: productParams({ productType, courseId, programId }),
  });
  return camelCaseObject(data).subscriptions || [];
};

/**
 * Start a subscription checkout. Returns the hosted Stripe checkout URL the
 * caller should redirect the learner to.
 */
export const startSubscription = async ({
  productType, courseId, programId, interval = 'month', mode = 'honor', successUrl, cancelUrl,
}) => {
  const body = {
    ...productParams({ productType, courseId, programId }),
    interval,
    mode,
    success_url: successUrl,
    cancel_url: cancelUrl,
  };
  const { data } = await client().post(`${baseUrl()}/subscribe/`, snakeCaseObject(body));
  return camelCaseObject(data);
};

/** Hosted billing-portal URL (update card / cancel). */
export const getBillingPortalUrl = async ({ returnUrl } = {}) => {
  const { data } = await client().post(`${baseUrl()}/billing_portal/`, snakeCaseObject({ returnUrl }));
  return camelCaseObject(data).url || null;
};
